import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AttachmentsInline } from "@/components/EmailComposer";
import {
  Mail, Plus, Pencil, Trash2, Loader2, Save, Paperclip, Store, Users, Newspaper, Layers, Languages,
} from "lucide-react";

const MODULE_OPTS = [
  { v: "", label: "Generico", icon: Layers },
  { v: "rivenditori", label: "Rivenditori", icon: Store },
  { v: "ambassador", label: "Ambassador", icon: Users },
  { v: "redazionali", label: "Redazionali", icon: Newspaper },
];
const MODULE_LABEL = { "": "Generico", rivenditori: "Rivenditori", ambassador: "Ambassador", redazionali: "Redazionali" };

const VARS = [
  { token: "{{nome_attività}}", label: "Nome attività" },
  { token: "{{città}}", label: "Città" },
  { token: "{{regione}}", label: "Regione" },
  { token: "{{categoria}}", label: "Categoria" },
  { token: "{{nazione}}", label: "Nazione" },
];

const emptyForm = { id: null, title: "", module: "", subject: "", body: "", attachments: [], language: "it" };
const TPL_LANGS = [
  { value: "it", label: "Italiano" },
  { value: "en", label: "Inglese" },
  { value: "fr", label: "Francese" },
  { value: "es", label: "Spagnolo" },
  { value: "de", label: "Tedesco" },
];
const LANG_LABEL = Object.fromEntries(TPL_LANGS.map((l) => [l.value, l.label]));

export function TemplateCenter() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMod, setActiveMod] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [copyLang, setCopyLang] = useState("en");
  const [translating, setTranslating] = useState(false);
  const bodyRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/message-templates?channel=email")
      .then(({ data }) => setTemplates(data.templates || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Caricamento modelli fallito"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...emptyForm, module: activeMod === "all" ? "" : activeMod }); setCopyLang("en"); setOpen(true); };
  const openEdit = (t) => {
    const lang = (t.language || "it").toLowerCase();
    setCopyLang(lang === "en" ? "de" : "en");
    setForm({
      id: t._id, title: t.title || "", module: (t.module || ""), language: (t.language || "it").toLowerCase(),
      subject: t.subject || "", body: t.body || "", attachments: t.attachments || [],
    });
    setOpen(true);
  };

  const translatedCopy = async () => {
    if (!form.body.trim() && !form.subject.trim()) { toast.error("Serve un testo da tradurre"); return; }
    if (copyLang === form.language) { toast.error("Scegli una lingua diversa da quella del modello"); return; }
    setTranslating(true);
    try {
      const { data } = await api.post("/translate-message", { text: form.body, subject: form.subject, target_lang: copyLang });
      const baseTitle = form.title.trim().replace(/\s·\s[A-Z]{2}$/, "") || "Modello";
      await api.post("/message-templates", {
        title: `${baseTitle} · ${copyLang.toUpperCase()}`, channel: "email", subject: data.subject,
        body: data.text, module: form.module, attachments: form.attachments, language: copyLang,
      });
      toast.success(`Copia tradotta in ${LANG_LABEL[copyLang]} salvata`);
      setOpen(false); setForm(emptyForm); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Traduzione fallita");
    } finally { setTranslating(false); }
  };

  const insertVar = (token) => {
    const el = bodyRef.current;
    if (!el) { setForm((f) => ({ ...f, body: (f.body || "") + token })); return; }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = form.body.slice(0, start) + token + form.body.slice(end);
    setForm((f) => ({ ...f, body: next }));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Dai un titolo al modello"); return; }
    if (!form.body.trim() && !form.attachments.length) { toast.error("Aggiungi un testo o almeno un allegato"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(), channel: "email", subject: form.subject,
        body: form.body, module: form.module, attachments: form.attachments, language: form.language,
      };
      if (form.id) await api.patch(`/message-templates/${form.id}`, payload);
      else await api.post("/message-templates", payload);
      toast.success(form.id ? "Modello aggiornato" : "Modello creato");
      setOpen(false); setForm(emptyForm); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Eliminare il modello "${t.title}"?`)) return;
    try {
      await api.delete(`/message-templates/${t._id}`);
      toast.success("Modello eliminato");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Eliminazione fallita"); }
  };

  const countFor = (v) => templates.filter((t) => (t.module || "") === v).length;
  const shown = activeMod === "all" ? templates : templates.filter((t) => (t.module || "") === activeMod);

  return (
    <div data-testid="template-center">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 rounded-xl border border-brand/30 bg-brand/10 p-4">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
          <p className="max-w-2xl text-sm text-foreground">
            Archivio dei <b>modelli d'invio</b>: testo predefinito + <b>allegati propri del modello</b> (Catalogo PDF, Deck, Lookbook…).
            Usa le variabili <code className="rounded bg-brema-bg px-1">{"{{nome_attività}}"}</code>, <code className="rounded bg-brema-bg px-1">{"{{città}}"}</code>: verranno sostituite col contatto reale <b>all'invio</b>.
            Selezionando un modello nei composer, testo e allegati vengono applicati subito.
          </p>
        </div>
        <Button onClick={openNew} data-testid="template-new-btn"
          className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
          <Plus className="mr-2 h-4 w-4" /> Nuovo modello
        </Button>
      </div>

      <Tabs value={activeMod} onValueChange={setActiveMod} data-testid="template-module-tabs">
        <TabsList className="flex-wrap bg-brema-card">
          <TabsTrigger value="all" data-testid="template-tab-all">Tutti
            <span className="ml-2 rounded-full bg-brema-bg px-1.5 text-[10px] text-muted-foreground">{templates.length}</span>
          </TabsTrigger>
          {MODULE_OPTS.map((o) => (
            <TabsTrigger key={o.v || "generic"} value={o.v} data-testid={`template-tab-${o.v || "generic"}`}>
              <o.icon className="mr-1.5 h-4 w-4" /> {o.label}
              <span className="ml-2 rounded-full bg-brema-bg px-1.5 text-[10px] text-muted-foreground">{countFor(o.v)}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="template-loading">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Caricamento modelli…
          </div>
        ) : shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-brema-card p-10 text-center" data-testid="template-empty">
            <Mail className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nessun modello in questa sezione. Crea il primo con "Nuovo modello".</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {shown.map((t) => (
              <div key={t._id} data-testid="template-card" className="flex flex-col rounded-xl border border-border bg-brema-card p-4">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-foreground">{t.title}</p>
                    <span className="mt-1 inline-flex items-center gap-1.5">
                      <span className="rounded-full bg-brema-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">
                        {MODULE_LABEL[t.module || ""]}
                      </span>
                      {t.language && (
                        <span data-testid="template-lang-badge" className="inline-flex items-center gap-1 rounded-full border border-border bg-brema-card px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground">
                          <Languages className="h-3 w-3 text-brand" /> {String(t.language).toUpperCase()}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button size="sm" variant="secondary" data-testid="template-edit-btn" onClick={() => openEdit(t)}
                      className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-bg">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="danger" data-testid="template-delete-btn" onClick={() => del(t)}
                      className="rounded-full text-xs">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {t.subject && <p className="text-xs text-muted-foreground"><b>Oggetto:</b> {t.subject}</p>}
                {t.body && <p className="mt-1.5 line-clamp-3 whitespace-pre-line rounded-md bg-brema-bg p-2 text-xs text-foreground">{t.body}</p>}
                {(t.attachments || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="template-card-attachments">
                    {t.attachments.map((a) => (
                      <span key={a.storage_path} className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] text-brand">
                        <Paperclip className="h-3 w-3" /> {a.filename}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setForm(emptyForm); } }}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto border-border bg-brema-card" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{form.id ? "Modifica modello" : "Nuovo modello"}</DialogTitle>
            <DialogDescription>Testo + allegati che verranno applicati insieme all'invio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Titolo</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  data-testid="template-title-input" placeholder="Es. Proposta collaborazione rivenditori" />
              </div>
              <div className="space-y-1.5">
                <Label>Modulo</Label>
                <Select value={form.module || "__generic"} onValueChange={(v) => setForm((f) => ({ ...f, module: v === "__generic" ? "" : v }))}>
                  <SelectTrigger data-testid="template-module-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__generic">Generico (visibile ovunque)</SelectItem>
                    <SelectItem value="rivenditori">Rivenditori</SelectItem>
                    <SelectItem value="ambassador">Ambassador</SelectItem>
                    <SelectItem value="redazionali">Redazionali</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1"><Languages className="h-3.5 w-3.5" /> Lingua</Label>
                <Select value={form.language || "it"} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
                  <SelectTrigger data-testid="template-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TPL_LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Oggetto</Label>
              <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                data-testid="template-subject-input" placeholder="Es. Nuova collezione Brema per {{città}}" />
            </div>
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <Label className="mr-1">Testo</Label>
                {VARS.map((v) => (
                  <button key={v.token} type="button" onClick={() => insertVar(v.token)} data-testid={`template-var-${v.label}`}
                    className="rounded-full border border-border bg-brema-bg px-2 py-0.5 text-[11px] text-brand hover:bg-brand/10">
                    + {v.label}
                  </button>
                ))}
              </div>
              <Textarea ref={bodyRef} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                data-testid="template-body-input" rows={8} className="resize-y"
                placeholder={"Ciao {{nome_attività}},\n\n…"} />
            </div>
            <div className="space-y-1.5">
              <Label>Allegati del modello</Label>
              <AttachmentsInline attachments={form.attachments}
                onAdd={(r) => setForm((f) => ({ ...f, attachments: [...f.attachments, r] }))}
                onRemove={(sp) => setForm((f) => ({ ...f, attachments: f.attachments.filter((a) => a.storage_path !== sp) }))}
                testidPrefix="template-att" />
            </div>
            <div className="rounded-xl border border-dashed border-border bg-brema-bg/50 p-3" data-testid="template-translate-block">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground"><Languages className="h-3.5 w-3.5 text-brand" /> Crea una copia tradotta di questo modello</p>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={copyLang} onValueChange={setCopyLang}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid="template-copy-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TPL_LANGS.filter((l) => l.value !== form.language).map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="secondary" size="sm" onClick={translatedCopy} disabled={translating || saving || (!form.body.trim() && !form.subject.trim())}
                  data-testid="template-translate-copy-btn"
                  className="rounded-full border border-border bg-transparent text-xs font-semibold text-foreground hover:bg-brema-hover disabled:opacity-40">
                  {translating ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Traduzione…</> : <><Languages className="mr-1.5 h-3.5 w-3.5 text-brand" /> Traduci e salva copia</>}
                </Button>
                <span className="text-[11px] text-muted-foreground">Nuovo modello "{form.title.trim() || "Modello"} · {copyLang.toUpperCase()}" con stessi allegati e modulo.</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setOpen(false); setForm(emptyForm); }} data-testid="template-cancel-btn">Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="template-save-btn"
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva modello</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
